/** The first-run wizard and the "Get started" checklist. */
const onboarding = {
  en: {
    wizard: {
      title: "Let's set up your shop",
      stepOf: "Step {number} of {total}",
      skipAll: "Skip setup",
      back: "Back",
      next: "Continue",
      saving: "Saving…",
      saveFailed: "Could not save this. Check the details and try again.",
      details: {
        title: "About your shop",
        intro: "Customers see this on your menu and your receipts. You can change it any time.",
        name: "Shop name",
        phone: "Phone",
        address: "Address",
        currency: "Currency",
        language: "Language of your shop",
        logo: "Logo (optional)",
        chooseLogo: "Choose a picture",
      },
      modules: {
        title: "What do you need?",
        intro: "Pick what fits. You can change all of this later, and nothing is ever lost.",
      },
      products: {
        title: "Your products",
        intro: "The fastest way to a full shop: pick what you sell from our catalogue instead of typing it all.",
        count: { one: "You have {count} product so far.", other: "You have {count} products so far." },
        pick: "Choose from the catalogue",
        copy: "Copy from {name}",
        later: "I'll add them myself",
      },
      ready: {
        title: "You're ready to sell",
        intro: "Ring up your first sale to see it all come together.",
        register: "Open the register",
        dashboard: "Go to my dashboard",
        pinNote: "Your till PIN is set. You can change it, and add your team, from the Staff screen.",
      },
    },
    card: {
      title: "Get started",
      progress: "{done} of {total} done",
      hide: "Hide",
      hideConfirm: "Hide this checklist? You can still do each step from its own screen.",
      optional: "optional",
      help: "Help",
      go: "Go",
      steps: {
        products: { title: "Add your products", desc: "Pick from the catalogue or add your own.", cta: "Add products" },
        buy_prices: { title: "Set buy prices", desc: "Without them, margins show a warning. Start with your best sellers.", cta: "Set prices" },
        device: { title: "Set up your register device", desc: "The phone or tablet you sell from.", cta: "Devices" },
        first_sale: { title: "Make your first sale", desc: "Ring up anything at the register.", cta: "Open register" },
        staff: { title: "Add someone to your team", desc: "Give a cashier their own PIN.", cta: "Add staff" },
        menu_qr: { title: "Print your menu QR card", desc: "Customers scan it to see your products.", cta: "Print card" },
      },
    },
  },
  fr: {
    wizard: {
      title: "Configurons votre boutique",
      stepOf: "Étape {number} sur {total}",
      skipAll: "Passer la configuration",
      back: "Retour",
      next: "Continuer",
      saving: "Enregistrement…",
      saveFailed: "Impossible d'enregistrer. Vérifiez les informations et réessayez.",
      details: {
        title: "À propos de votre boutique",
        intro: "Vos clients le voient sur votre menu et vos reçus. Vous pourrez le modifier à tout moment.",
        name: "Nom de la boutique",
        phone: "Téléphone",
        address: "Adresse",
        currency: "Devise",
        language: "Langue de votre boutique",
        logo: "Logo (facultatif)",
        chooseLogo: "Choisir une image",
      },
      modules: {
        title: "De quoi avez-vous besoin ?",
        intro: "Choisissez ce qui vous convient. Vous pourrez tout changer plus tard, sans jamais rien perdre.",
      },
      products: {
        title: "Vos produits",
        intro: "Le moyen le plus rapide d'avoir une boutique pleine : choisissez ce que vous vendez dans notre catalogue au lieu de tout saisir.",
        count: { one: "Vous avez {count} produit pour l'instant.", other: "Vous avez {count} produits pour l'instant." },
        pick: "Choisir dans le catalogue",
        copy: "Copier depuis {name}",
        later: "Je les ajouterai moi-même",
      },
      ready: {
        title: "Vous êtes prêt à vendre",
        intro: "Faites votre première vente pour voir tout fonctionner ensemble.",
        register: "Ouvrir la caisse",
        dashboard: "Aller à mon tableau de bord",
        pinNote: "Votre code PIN de caisse est défini. Vous pouvez le changer, et ajouter votre équipe, depuis l'écran Équipe.",
      },
    },
    card: {
      title: "Pour bien démarrer",
      progress: "{done} sur {total} faits",
      hide: "Masquer",
      hideConfirm: "Masquer cette liste ? Vous pourrez toujours faire chaque étape depuis son écran.",
      optional: "facultatif",
      help: "Aide",
      go: "Y aller",
      steps: {
        products: { title: "Ajoutez vos produits", desc: "Choisissez dans le catalogue ou ajoutez les vôtres.", cta: "Ajouter des produits" },
        buy_prices: { title: "Saisissez les prix d'achat", desc: "Sans eux, les marges affichent un avertissement. Commencez par vos meilleures ventes.", cta: "Saisir les prix" },
        device: { title: "Configurez votre appareil de caisse", desc: "Le téléphone ou la tablette avec lequel vous vendez.", cta: "Appareils" },
        first_sale: { title: "Faites votre première vente", desc: "Encaissez n'importe quoi à la caisse.", cta: "Ouvrir la caisse" },
        staff: { title: "Ajoutez quelqu'un à votre équipe", desc: "Donnez à un caissier son propre code PIN.", cta: "Ajouter" },
        menu_qr: { title: "Imprimez votre carte QR du menu", desc: "Les clients la scannent pour voir vos produits.", cta: "Imprimer" },
      },
    },
  },
  ar: {
    wizard: {
      title: "لنُعدّ متجرك",
      stepOf: "الخطوة {number} من {total}",
      skipAll: "تخطّي الإعداد",
      back: "رجوع",
      next: "متابعة",
      saving: "جارٍ الحفظ…",
      saveFailed: "تعذّر الحفظ. تحقق من البيانات وحاول مرة أخرى.",
      details: {
        title: "عن متجرك",
        intro: "يراه الزبائن في قائمتك وفواتيرك. يمكنك تغييره في أي وقت.",
        name: "اسم المتجر",
        phone: "الهاتف",
        address: "العنوان",
        currency: "العملة",
        language: "لغة متجرك",
        logo: "الشعار (اختياري)",
        chooseLogo: "اختيار صورة",
      },
      modules: {
        title: "ماذا تحتاج؟",
        intro: "اختر ما يناسبك. يمكنك تغيير كل هذا لاحقًا ولن يضيع شيء أبدًا.",
      },
      products: {
        title: "منتجاتك",
        intro: "أسرع طريق إلى متجر ممتلئ: اختر ما تبيعه من الكتالوج بدل كتابته كله.",
        count: { zero: "ليس لديك منتجات بعد.", one: "لديك منتج واحد حتى الآن.", two: "لديك منتجان حتى الآن.", few: "لديك {count} منتجات حتى الآن.", many: "لديك {count} منتجًا حتى الآن.", other: "لديك {count} منتج حتى الآن." },
        pick: "الاختيار من الكتالوج",
        copy: "النسخ من {name}",
        later: "سأضيفها بنفسي",
      },
      ready: {
        title: "أنت جاهز للبيع",
        intro: "أجرِ أول عملية بيع لترى كل شيء يعمل معًا.",
        register: "فتح الصندوق",
        dashboard: "الذهاب إلى لوحة التحكم",
        pinNote: "تم تحديد رمز PIN الخاص بالصندوق. يمكنك تغييره وإضافة فريقك من شاشة الفريق.",
      },
    },
    card: {
      title: "للبدء",
      progress: "تم {done} من {total}",
      hide: "إخفاء",
      hideConfirm: "إخفاء هذه القائمة؟ يمكنك دائمًا إتمام كل خطوة من شاشتها.",
      optional: "اختياري",
      help: "مساعدة",
      go: "اذهب",
      steps: {
        products: { title: "أضف منتجاتك", desc: "اختر من الكتالوج أو أضف منتجاتك.", cta: "إضافة منتجات" },
        buy_prices: { title: "حدّد أسعار الشراء", desc: "بدونها يظهر تحذير في الهوامش. ابدأ بالأكثر مبيعًا.", cta: "تحديد الأسعار" },
        device: { title: "جهّز جهاز الصندوق", desc: "الهاتف أو اللوحي الذي تبيع منه.", cta: "الأجهزة" },
        first_sale: { title: "أجرِ أول عملية بيع", desc: "سجّل أي عملية بيع في الصندوق.", cta: "فتح الصندوق" },
        staff: { title: "أضف شخصًا إلى فريقك", desc: "امنح أحد البائعين رمز PIN خاصًا به.", cta: "إضافة" },
        menu_qr: { title: "اطبع بطاقة رمز QR للقائمة", desc: "يمسحها الزبائن لرؤية منتجاتك.", cta: "طباعة" },
      },
    },
  },
};

export default onboarding;
